/**
 * @file Primitive Editor for basic data types
 * @version 2.0.0
 */

import BaseEditor from './BaseEditor.js';
import { INPUT_TYPES, getInputTypeByName } from './input-types/index.js';
import { registerEditor } from './component-registry.js';

const CONTROLS_TEMPLATE = `
  <!-- Click-edit trigger -->
  <button
    v-if="showClickEditTrigger"
    class="btn btn-outline-secondary btn-sm"
    @click="startEdit()"
  >
    <i class="bi bi-pencil"></i>
  </button>

  <!-- Add button for undefined -->
  <button
    v-if="showAddButton"
    @click="addValue"
    class="btn btn-primary btn-sm"
  >
    <i class="bi bi-plus-circle"></i>
    <span v-if="addLabel"> {{ addLabel }}</span>
  </button>

  <!-- Normal controls when not showing add button -->
  <template v-if="isActiveEditing">
    <slot name="before-controls"></slot>

    <button
      v-if="showDeleteButton"
      @click="handleDelete"
      class="btn btn-danger btn-sm"
    >
      <i class="bi bi-trash"></i>
      <span v-if="displayButtonsLabels"> Delete</span>
    </button>

    <button
      v-if="showSaveButton"
      @click="saveEdit"
      class="btn btn-success btn-sm"
      :disabled="!canSave"
    >
      <i class="bi bi-check"></i>
      <span v-if="displayButtonsLabels"> Save</span>
    </button>

    <button
      v-if="showResetButton"
      @click="cancelEdit"
      class="btn btn-secondary btn-sm"
      :disabled="!hasChanges"
    >
      <i class="bi bi-arrow-counterclockwise"></i>
      <span v-if="displayButtonsLabels"> Reset</span>
    </button>

    <button
      v-if="showCancelButton"
      @click="cancelEdit"
      class="btn btn-secondary btn-sm"
    >
      <i class="bi bi-x"></i>
      <span v-if="displayButtonsLabels"> Cancel</span>
    </button>
  </template>

  <slot name="controls"></slot>
`;

export { CONTROLS_TEMPLATE };

const PrimitiveEditor = {
  name: 'PrimitiveEditor',
  extends: BaseEditor,

  setup(props, ctx) {
    return BaseEditor.setup(props, ctx);
  },

  data() {
    return {
      fieldId: Math.random().toString(36).substr(2, 9),
      addLabel: null,
    };
  },

  computed: {
    /**
     * Get current input type definition
     */
    currentType() {
      const inputTypeAttr = this.getAttribute('input_type');

      if (typeof inputTypeAttr === 'string') {
        const found = getInputTypeByName(inputTypeAttr);
        if (found) return found;
      } else if (typeof inputTypeAttr === 'object' && inputTypeAttr !== null) {
        return inputTypeAttr;
      }

      return (
        INPUT_TYPES.find(type => type.matches(this.getAttribute)) ||
        INPUT_TYPES[INPUT_TYPES.length - 1]
      );
    },

    /**
     * Current display mode
     */
    currentMode() {
      if (!this.getAttribute('readable', true)) return 'hidden';
      if (!this.getAttribute('editable', true)) return 'display';
      if (this.mode === 'edit' || this.mode === 'form') return 'edit';
      if (this.mode === 'click-edit' && this.isEditing) return 'edit';
      return 'display';
    },

    /**
     * Text color classes for value display
     */
    valueTextClass() {
      if (this.validationError) {
        return 'text-danger';
      }
      if (
        this.reference !== undefined &&
        (this.internalValue.editable !== this.reference ||
          (this.internalValue.editable === undefined &&
            this.reference === null))
      ) {
        return this.getAttribute('updatedClass', 'text-info field-updated');
      }
      return '';
    },

    /**
     * Display component from current input type
     */
    displayComponent() {
      return this.currentType.displayComponent;
    },

    /**
     * Edit component from current input type
     */
    editComponent() {
      return this.currentType.editComponent;
    },

    /**
     * External state for parent communication
     */
    externalState() {
      const state = {};

      if (this.mode === 'click-edit' && this.isEditing) {
        state.state = 'no_change';
      } else {
        if (this.hasErrors) {
          state.state = 'error';
        } else if (this.internalValue.isModified) {
          state.state = 'updated';
          state.update = () => this.internalValue.save();
        } else {
          state.state = 'no_change';
        }
      }

      Object.defineProperty(state, 'sender', {
        value: this,
        enumerable: false,
        writable: false,
      });

      return state;
    },
  },

  methods: {
    /**
     * Handle input changes
     * @param event
     */
    handleInput(event) {
      this.internalValue.editable = event.target.value;
    },

    /**
     * Handle keyboard events
     * @param event
     */
    handleKeydown(event) {
      if (this.mode === 'click-edit' && this.isEditing) {
        if (event.key === 'Enter' && !event.shiftKey) {
          event.preventDefault();
          this.saveEdit();
        } else if (event.key === 'Escape') {
          event.preventDefault();
          this.reset();
        }
      }
    },

    /**
     * Handle blur events
     */
    handleBlur() {
      if (this.mode === 'click-edit' && this.isEditing) {
        if (!this.internalValue.isModified) {
          setTimeout(() => {
            if (this.isEditing) {
              this.reset();
            }
          }, 150);
        }
      }
    },
  },

  template: `
  <div v-if="currentMode !== 'hidden'" v-bind="$attrs">
    <div class="d-flex align-items-baseline gap-2">
      <span
        v-if="getAttribute('displayLabel', true)"
        class="fw-medium"
        :style="{ flexBasis: getAttribute('labelWidth', 'fit-content') }"
      >
        {{ editorTitle }}
      </span>

      <!-- Undefined value -->
      <div v-if="isUndefined" class="flex-grow-1">
        <span class="text-muted fst-italic">—</span>
      </div>

      <!-- Display mode -->
      <template v-if="currentMode === 'display' && !isUndefined">
        <div
          class="d-flex flex-grow-1"
          :class="{ 'bg-light': mode === 'click-edit' }"
          style="flex-basis: 150px;"
          :style="mode === 'click-edit' ? 'cursor: pointer;' : ''"
          @click="() => mode === 'click-edit' ? startEdit() : null"
        >
          <component
            :is="displayComponent"
            :value="internalValue.editable"
            :getAttribute="getAttribute"
            :class="valueTextClass"
          />
        </div>
      </template>

      <!-- Edit mode -->
      <div
        v-if="currentMode !== 'display' && !isUndefined"
        style="flex-basis: 150px;"
        class="flex-grow-1"
      >
        <component
          ref="input"
          :is="editComponent"
          :value="internalValue.editable"
          :getAttribute="getAttribute"
          :validationError="validationError"
          :fieldId="fieldId"
          :class="valueTextClass"
          @input="handleInput"
          @keydown="handleKeydown"
          @blur="handleBlur"
        />
      </div>

      <!-- Controls -->
      <div v-if="currentMode !== 'display' || mode === 'click-edit'" class="d-flex gap-1 align-items-center">
        ${CONTROLS_TEMPLATE}
      </div>
    </div>

    <!-- Error message -->
    <div
      v-if="hasErrors && currentMode === 'edit'"
      class="invalid-feedback d-block mt-1"
      style="flex-basis: 150px;"
    >
      {{ validationError }}
    </div>
  </div>
  `,
};

registerEditor('primitive', 'default', PrimitiveEditor);

export default PrimitiveEditor;
